/**
 * Compose a letter, document request or email from a record (WPS-175 #3,
 * WPS-220): any record the composer reads (`/api/composer/<recordType>/...`).
 *
 * One surface, two panes: every template of the chosen kind on the left, the
 * chosen one and who it goes to on the right. Penny's flow is "pick the
 * letter, pick the contact, send", and splitting that across stacked modals
 * made the user hold the letter in their head while answering about contacts.
 *
 * Nothing here names a record type or a claim type. The left pane's groups and
 * its "when to use" labels are the tenant's own workflow states and actions,
 * served by `templates/`; who it can go to comes from the send preview, and a
 * template's fill-ins come from its own `prompts`.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNavigate } from 'react-router'
import { Loader2, Mail, MapPin, Paperclip, Pencil, Plus, Upload, X } from 'lucide-react'
import { z } from 'zod'

import { EmailEditor } from '@/admin/components/email-editor'
import { MergeFieldText } from '@/admin/components/communications/merge-field-text'
import { LetterPage, TemplateRail } from '@/admin/components/composer/letter-desk-parts'
import {
  asLetterPage,
  tidyForEditing,
  useLetterCategories,
  type LetterPageValue,
} from '@/admin/components/composer/letter-desk'
import { useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useComposerSend,
  useComposerSendPreview,
  useComposerTemplatePreview,
  useComposerTemplates,
  useUploadSendAttachment,
  type ComposeKind,
  type ComposerRecipient,
  type ComposerTemplate,
  type ComposerTemplatePreview,
  type LetterDelivery,
  type LetterEdit,
  type LetterPrompt,
  type Party,
} from '@/admin/hooks/use-composer'
import { useSubjectAttachments } from '@/admin/hooks/use-subject-attachments'
import { formatBytes } from '@/shared/components/evidence/evidence-file-utils'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { Textarea } from '@/shared/components/ui/textarea'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'

const SECTION_LABEL =
  'text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Form field names for a template's fill-ins.
const PROMPT_FIELD_PREFIX = 'prompt__'
// The send endpoint's own limits on files that go with a letter.
const MAX_ATTACHMENTS = 10
const MAX_ATTACHMENT_MB = 15
const ATTACH_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*'

interface AttachmentPick {
  uuid: string
  name: string
  size: number
  /** Uploaded for this send, rather than one of the record's own files. */
  upload: boolean
}

function asPicks(value: unknown): AttachmentPick[] {
  return Array.isArray(value) ? (value as AttachmentPick[]) : []
}

/** The words for each kind the composer sends. */
function composeCopy(t: TFunction, kind: ComposeKind) {
  if (kind === 'evidence_request') {
    return {
      title: t('claimCompose.request.title', 'New document request'),
      search: t('claimCompose.request.search', 'Search document requests...'),
      empty: t('claimCompose.request.empty', 'No document requests have been added yet.'),
      noMatches: t('claimCompose.request.noMatches', 'No document requests match that search.'),
      library: t('claimCompose.request.library', 'All document requests'),
      pick: t('claimCompose.request.pick', 'Choose a document request to get started.'),
      send: t('claimCompose.request.send', 'Send request'),
      sent: (count: number) =>
        t('claimCompose.request.sent', 'Document request sent to {{count}} recipient(s).', { count }),
      errorSend: t('claimCompose.request.errorSend', 'Could not send the document request.'),
      edit: t('claimCompose.request.edit', 'Edit this request'),
    }
  }
  if (kind === 'notification') {
    return {
      title: t('claimCompose.email.title', 'New email'),
      search: t('claimCompose.email.search', 'Search emails...'),
      empty: t('claimCompose.email.empty', 'No emails have been added yet.'),
      noMatches: t('claimCompose.email.noMatches', 'No emails match that search.'),
      library: t('claimCompose.email.library', 'All emails'),
      pick: t('claimCompose.email.pick', 'Choose an email to get started.'),
      send: t('claimCompose.email.send', 'Send email'),
      sent: (count: number) =>
        t('claimCompose.email.sent', 'Email sent to {{count}} recipient(s).', { count }),
      errorSend: t('claimCompose.email.errorSend', 'Could not send the email.'),
      edit: t('claimCompose.email.edit', 'Edit this email'),
    }
  }
  return {
    title: t('claimLetter.title', 'New letter'),
    search: t('claimLetter.search', 'Search letters...'),
    empty: t('claimLetter.empty', 'No letters have been added yet.'),
    noMatches: t('claimLetter.noMatches', 'No letters match that search.'),
    library: t('claimCompose.letter.library', 'All letters'),
    pick: t('claimLetter.pickPrompt', 'Choose a letter to get started.'),
    send: t('claimLetter.send', 'Send letter'),
    sent: (count: number) =>
      t('claimCompose.letter.sent', 'Letter sent to {{count}} recipient(s).', { count }),
    errorSend: t('claimLetter.errorSend', 'Could not send the letter.'),
    edit: t('claimLetter.editLetter', 'Edit this letter'),
  }
}

type ComposeCopy = ReturnType<typeof composeCopy>

/** A template's subject, its merge fields shown as quiet tags. */
function SubjectPreview({ subject }: { subject: string }) {
  return <MergeFieldText text={subject} className="text-[13px] text-[var(--ink-2)]" />
}

export function RecordComposerDialog({
  open,
  onOpenChange,
  recordType,
  recordUuid,
  kind = 'letter',
  canAttachFiles = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The record type the composer reads, e.g. `claim`. */
  recordType: string
  recordUuid: string
  kind?: ComposeKind
  /** A letter can carry the record's files, or ones uploaded for the send. */
  canAttachFiles?: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const authoringEnabled = useFeatureFlag('email_authoring')
  const copy = composeCopy(t, kind)

  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const isLetterDesk = kind === 'letter'

  const { data, isPending, isError, refetch } = useComposerTemplates(
    recordType,
    open ? recordUuid : undefined,
    kind,
  )

  // Letters are filed by category (the template's first tag), with the
  // record's own workflow letters first.
  const categories = useLetterCategories(data?.groups ?? [], {
    suggested: t('claimLetter.suggested', 'For this record'),
    unfiled: t('claimLetter.unfiled', 'Other letters'),
  })
  // Emails and requests under the workflow that sends them, then the library.
  const workflowGroups = useMemo(
    () =>
      (data?.groups ?? [])
        .filter((group) => group.items.length > 0)
        .map((group) => ({
          key: group.key,
          label: group.key === 'library' ? copy.library : group.label,
          items: group.items,
        })),
    [data, copy.library],
  )
  const sections = isLetterDesk ? categories : workflowGroups

  const selected: ComposerTemplate | undefined = useMemo(() => {
    for (const group of data?.groups ?? []) {
      const hit = group.items.find((item) => item.id === selectedId)
      if (hit) return hit
    }
    return undefined
  }, [data, selectedId])

  function close(next: boolean) {
    if (!next) {
      setSearch('')
      setSelectedId(null)
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className={
          isLetterDesk
            ? 'max-w-[1200px] sm:max-w-[1200px]'
            : 'max-w-[1040px] sm:max-w-[1040px]'
        }
      >
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>

        <div className="grid max-h-[78vh] min-h-[520px] grid-cols-1 gap-0 md:grid-cols-[270px_1fr]">
          {/* Left: everything this record can send, each under its heading. */}
          <div className="flex min-h-0 flex-col overflow-y-auto border-b-[0.5px] border-b-[var(--rule)] md:border-r-[0.5px] md:border-b-0 md:border-r-[var(--rule)]">
            <TemplateRail
              sections={sections}
              search={search}
              onSearch={setSearch}
              selectedId={selectedId}
              onSelect={setSelectedId}
              loading={isPending}
              error={isError}
              onRetry={() => refetch()}
              searchPlaceholder={copy.search}
              emptyLabel={copy.empty}
              noMatchesLabel={copy.noMatches}
              footer={
                authoringEnabled && can('templates.edit.global') ? (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/settings/email-templates')}
                    className="h-auto justify-start rounded-[6px] px-2 py-1.5 text-[12px] font-light text-[var(--green)]"
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    {t('claimLetter.createTemplate', 'Create template')}
                  </Button>
                ) : null
              }
            />
          </div>
          {/* Right: the chosen template, and who receives it. */}
          <div className="min-w-0 overflow-y-auto px-5 py-4">
            {!selected ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                <Mail className="h-5 w-5 text-[var(--ink-3)]" />
                <p className="text-[13px] text-[var(--ink-2)]">{copy.pick}</p>
              </div>
            ) : (
              // Keyed so a fresh pick starts clean: an edit or a fill-in belongs
              // to one message and must not carry across to the next.
              <ComposeTemplate
                key={selected.id}
                recordType={recordType}
                recordUuid={recordUuid}
                canAttachFiles={canAttachFiles}
                template={selected}
                copy={copy}
                onDone={() => close(false)}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── The chosen template ─────────────────────────────────────────────

/** Picked record parties (by key), plus anyone typed in by hand. */
interface RecipientsValue {
  /** Mail posts the letter; its recipients need an address, not an email. */
  delivery: LetterDelivery
  parties: string[]
  someoneElse: boolean
  email: string
  name: string
  address: string
}

const EMPTY_RECIPIENTS: RecipientsValue = {
  delivery: 'email',
  parties: [],
  someoneElse: false,
  email: '',
  name: '',
  address: '',
}

function asRecipients(value: unknown): RecipientsValue {
  const v = (value ?? {}) as Partial<RecipientsValue>
  return {
    delivery: v.delivery === 'mail' ? 'mail' : 'email',
    parties: Array.isArray(v.parties) ? v.parties : [],
    someoneElse: v.someoneElse === true,
    email: typeof v.email === 'string' ? v.email : '',
    name: typeof v.name === 'string' ? v.name : '',
    address: typeof v.address === 'string' ? v.address : '',
  }
}

/** Whether a party can be reached the chosen way: an email, or an address. */
function reachable(party: Party, delivery: LetterDelivery): boolean {
  return delivery === 'mail' ? party.address !== '' : party.email !== ''
}

/** Who the preview addresses: the one picked party, else a typed name. */
function previewRecipientOf(value: RecipientsValue): { partyKey?: string; name?: string } {
  if (value.parties.length === 1 && !value.someoneElse) return { partyKey: value.parties[0] }
  if (value.parties.length === 0 && value.someoneElse && value.name.trim()) {
    return { name: value.name.trim() }
  }
  return {}
}

/** The fill-in errors a 400 from the send endpoint carries, keyed by prompt. */
function promptErrorsFrom(error: unknown): Record<string, string> | null {
  const result = (error as { response?: { data?: { result?: unknown } } })?.response?.data
    ?.result as { prompt_values?: Record<string, string> } | undefined
  return result?.prompt_values && typeof result.prompt_values === 'object'
    ? result.prompt_values
    : null
}

/** The first message a 400 from the send endpoint carries for one field. */
function fieldErrorFrom(error: unknown, field: string): string | null {
  const result = (error as { response?: { data?: { result?: unknown } } })?.response?.data
    ?.result as Record<string, unknown> | undefined
  const messages = result?.[field]
  return Array.isArray(messages) && typeof messages[0] === 'string' ? messages[0] : null
}

function ComposeTemplate({
  recordType,
  recordUuid,
  canAttachFiles,
  template,
  copy,
  onDone,
}: {
  recordType: string
  recordUuid: string
  canAttachFiles: boolean
  template: ComposerTemplate
  copy: ComposeCopy
  onDone: () => void
}) {
  const { t } = useTranslation()
  // `undefined` means untouched, distinct from "edited back to the original".
  const [edit, setEdit] = useState<LetterEdit | undefined>(undefined)
  const [editing, setEditing] = useState(false)
  // Until the sender types, the editor follows the message as the record fills
  // it in - a newly picked addressee's name in the greeting, say.
  const [editTyped, setEditTyped] = useState(false)
  const [previewRecipient, setPreviewRecipient] = useState<{
    partyKey?: string
    name?: string
  }>({})
  const [promptDraft, setPromptDraft] = useState<Record<string, string>>({})
  const promptValues = useDebouncedValue(promptDraft, 400)

  // Resolves who on the record the letter can go to.
  const preview = useComposerSendPreview(recordType, recordUuid, template.id)
  const letter = useComposerTemplatePreview(
    recordType,
    recordUuid,
    template.id,
    previewRecipient,
    edit,
    promptValues,
  )
  const sendPacket = useComposerSend(recordType, recordUuid, {
    successMessage: copy.sent,
    handleErrors: false,
  })

  const prompts: LetterPrompt[] = template.prompts ?? []
  const parties = preview.data?.parties ?? []
  const partiesRef = useRef(parties)
  partiesRef.current = parties
  const previewPendingRef = useRef(preview.isPending)
  previewPendingRef.current = preview.isPending
  const mailAvailableRef = useRef(false)
  mailAvailableRef.current = template.kind === 'letter' && !!preview.data?.mail_available

  const recipientsSchema = useMemo(
    () =>
      z
        .object({
          delivery: z.enum(['email', 'mail']),
          parties: z.array(z.string()),
          someoneElse: z.boolean(),
          email: z.string(),
          name: z.string(),
          address: z.string(),
        })
        .refine((v) => v.parties.length > 0 || v.someoneElse, {
          message: t('claimCompose.pickRecipient', 'Pick at least one person to send to.'),
        })
        .refine(
          (v) => !v.someoneElse || v.delivery === 'mail' || EMAIL_PATTERN.test(v.email.trim()),
          {
            message: t(
              'claimCompose.otherEmailRequired',
              'Enter an email address for the other recipient.',
            ),
          },
        )
        .refine(
          (v) =>
            !v.someoneElse ||
            v.delivery !== 'mail' ||
            (v.name.trim() !== '' && v.address.trim() !== ''),
          {
            message: t(
              'claimCompose.otherAddressRequired',
              'Enter a name and postal address for the other recipient.',
            ),
          },
        ),
    [t],
  )

  const attachmentsSchema = useMemo(
    () =>
      z
        .array(
          z.object({
            uuid: z.string(),
            name: z.string(),
            size: z.number(),
            upload: z.boolean(),
          }),
        )
        .refine((v) => v.length <= MAX_ATTACHMENTS, {
          message: t('claimCompose.attachTooMany', 'Attach at most {{count}} files.', {
            count: MAX_ATTACHMENTS,
          }),
        })
        .refine(
          (v) => v.reduce((total, f) => total + f.size, 0) <= MAX_ATTACHMENT_MB * 1024 * 1024,
          {
            message: t('claimCompose.attachTooBig', 'Attachments must total {{size}} MB or less.', {
              size: MAX_ATTACHMENT_MB,
            }),
          },
        ),
    [t],
  )
  const isLetter = template.kind === 'letter'
  // The letter as the record fills it in, for the page to start from.
  const letterDataRef = useRef<ComposerTemplatePreview | undefined>(letter.data)
  letterDataRef.current = letter.data

  const fields = useMemo<FormField[]>(
    () => [
      ...prompts.map<FormField>((prompt) => ({
        type:
          prompt.type === 'enum' ? 'select' : prompt.type === 'number' ? 'number' : 'text',
        name: `${PROMPT_FIELD_PREFIX}${prompt.key}`,
        label: prompt.label,
        value: '',
        testId: `compose-prompt-${prompt.key}`,
        placeholder:
          prompt.type === 'enum' ? t('claimCompose.promptChoose', 'Choose...') : undefined,
        required: prompt.required
          ? t('claimCompose.promptRequired', '{{label}} is required', { label: prompt.label })
          : undefined,
        options:
          prompt.type === 'enum'
            ? (prompt.options ?? []).map((option) => ({ value: option, label: option }))
            : undefined,
        onChange: (value: unknown) =>
          setPromptDraft((prev) => ({
            ...prev,
            [prompt.key]: value == null ? '' : String(value),
          })),
      })),
      // A letter is written on the page: date, addressee, address and
      // reference typed over in place, the body typed into like a document.
      ...(isLetter
        ? [
            {
              type: 'custom',
              name: 'letter',
              value: asLetterPage({}),
              render: (ctx) => (
                <LetterPageField
                  value={asLetterPage(ctx.values.letter)}
                  onChange={(next) => ctx.setFieldValue('letter', next)}
                  data={letterDataRef.current}
                  loading={!letterDataRef.current}
                  recordType={recordType}
                  recordUuid={recordUuid}
                  templateId={template.id}
                  error={ctx.errors.letter as string | undefined}
                />
              ),
            } satisfies FormField,
          ]
        : []),
      {
        type: 'custom',
        name: 'recipients',
        value: EMPTY_RECIPIENTS,
        validationSchema: recipientsSchema,
        render: (ctx) => (
          <RecipientPicker
            value={asRecipients(ctx.values.recipients)}
            error={ctx.errors.recipients as string | undefined}
            loading={previewPendingRef.current && partiesRef.current.length === 0}
            parties={partiesRef.current}
            mailAvailable={mailAvailableRef.current}
            onChange={(next) => {
              ctx.setFieldValue('recipients', next)
              // setFieldValue does not re-validate; clear a satisfied highlight.
              if (ctx.errors.recipients) ctx.setFieldError('recipients', undefined)
              setPreviewRecipient(previewRecipientOf(next))
            }}
          />
        ),
      },
      // Files go with letters only, from records that take them: the send
      // refuses them on anything else.
      ...(isLetter && canAttachFiles
        ? [
            {
              type: 'custom',
              name: 'attachments',
              value: [] as AttachmentPick[],
              validationSchema: attachmentsSchema,
              render: (ctx) => (
                <LetterAttachments
                  recordType={recordType}
                  recordUuid={recordUuid}
                  value={asPicks(ctx.values.attachments)}
                  error={ctx.errors.attachments as string | undefined}
                  onChange={(next) => {
                    ctx.setFieldValue('attachments', next)
                    if (ctx.errors.attachments) ctx.setFieldError('attachments', undefined)
                  }}
                />
              ),
            } satisfies FormField,
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template.id, recipientsSchema, attachmentsSchema, t],
  )

  const { Form } = useForm(
    { name: `compose-send-${template.id}`, submit: 'footer', fields },
    async (raw, ctx) => {
      const r = asRecipients(raw.recipients)
      const prompt_values = Object.fromEntries(
        prompts.map((prompt) => [
          prompt.key,
          String(raw[`${PROMPT_FIELD_PREFIX}${prompt.key}`] ?? '').trim(),
        ]),
      )
      const byPost = r.delivery === 'mail' && mailAvailableRef.current
      const recipients: ComposerRecipient[] = !r.someoneElse
        ? []
        : byPost
          ? [{ kind: 'third_party', name: r.name.trim(), postal_address: r.address.trim() }]
          : [{ kind: 'third_party', email: r.email.trim(), name: r.name.trim() || undefined }]
      // A letter sends what is on its page; anything else, the edit form's.
      const page = asLetterPage(raw.letter)
      const sendEdit: LetterEdit | undefined = isLetter
        ? {
            ...(page.touched.body ? { body_html_override: page.body } : {}),
            letter_header: {
              date: page.date,
              name: page.name,
              address: page.address,
              reference: page.reference,
            },
          }
        : edit
      try {
        await sendPacket.mutateAsync({
          recipients,
          template_id: template.id,
          edit: sendEdit,
          party_keys: r.parties,
          prompt_values,
          attachment_uuids: asPicks(raw.attachments).map((f) => f.uuid),
          delivery: byPost ? 'mail' : 'email',
        })
        onDone()
      } catch (error) {
        const promptErrors = promptErrorsFrom(error)
        if (promptErrors) {
          for (const [key, message] of Object.entries(promptErrors)) {
            ctx.setFieldError(`${PROMPT_FIELD_PREFIX}${key}`, message)
          }
          return
        }
        const attachmentError = fieldErrorFrom(error, 'attachment_uuids')
        if (attachmentError) {
          ctx.setFieldError('attachments', attachmentError)
          return
        }
        const deliveryError =
          fieldErrorFrom(error, 'delivery') ?? fieldErrorFrom(error, 'recipients')
        if (deliveryError) {
          ctx.setFieldError('recipients', deliveryError)
          return
        }
        toastApiError(error, copy.errorSend)
      }
    },
    // The custom fields read the record's parties and its filled-in letter:
    // refresh when they load.
    [template.id, parties, preview.isPending, letter.data],
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <h3 className="font-display text-[24px] leading-tight text-[var(--ink)]">
          {template.name}
        </h3>
        <SubjectPreview subject={template.subject} />
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {template.when_label && <Badge variant="secondary">{template.when_label}</Badge>}
          {template.audience === 'external' && (
            <Badge variant="destructive">{t('claimLetter.goesOutside', 'Goes outside')}</Badge>
          )}
          {template.audience === 'internal' && (
            <Badge variant="outline">{t('claimLetter.internalOnly', 'Internal only')}</Badge>
          )}
          {template.tags.map((tag) => (
            <Badge key={tag} variant="outline">
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      <Separator />

      {isLetter ? null : letter.isPending ? (
        <div className="flex items-center gap-2 py-6 text-[12px] text-[var(--ink-3)]">
          <Loader2 size={14} className="animate-spin" />
          {t('claimLetter.loadingPreview', 'Rendering the letter...')}
        </div>
      ) : letter.isError || !letter.data ? (
        <p className="rounded-md border-[0.5px] border-dashed border-[var(--rule)] px-4 py-5 text-center text-[12px] text-[var(--ink-3)]">
          {t('claimLetter.previewFailed', 'This letter could not be previewed.')}
        </p>
      ) : editing ? (
        <LetterEditForm
          key={
            editTyped
              ? 'typed'
              : `${letter.data.subject}|${letter.data.rendered_body_html ?? ''}`
          }
          // Filled in from the record - the claim number, dates and names a
          // sender reads and types over - rather than the template's fields.
          initialSubject={letter.data.subject || letter.data.source_subject}
          initialBody={tidyForEditing(
            letter.data.rendered_body_html || letter.data.source_body_html,
          )}
          onTyped={() => setEditTyped(true)}
          onApply={(next) => {
            setEdit(next)
            setEditing(false)
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <LetterPreview
            subject={letter.data.subject}
            bodyHtml={letter.data.body_html}
            pdfBase64={letter.data.pdf_base64 ?? ''}
          />
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setEditing(true)}
              className="h-auto px-0 text-[11px] text-[var(--green)]"
            >
              <Pencil className="mr-1 h-3 w-3" />
              {copy.edit}
            </Button>
            {edit && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setEdit(undefined)}
                className="h-auto px-0 text-[11px] text-[var(--ink-3)]"
              >
                <X className="mr-1 h-3 w-3" />
                {t('claimLetter.revertEdit', 'Revert to template')}
              </Button>
            )}
          </div>
        </div>
      )}

      {!isLetter && <Separator />}

      {prompts.length > 0 && (
        <p className={SECTION_LABEL}>{t('claimCompose.fillIn', 'Fill in')}</p>
      )}
      <Form className="flex flex-col gap-4">
        <div className="flex items-center gap-3 pt-1">
          {/* Enabled whatever the form holds: the click validates and points
              at what is missing. Only an in-flight send disables it. */}
          <Button type="submit" disabled={sendPacket.isPending} data-testid="compose-send">
            {sendPacket.isPending ? t('claimLetter.sending', 'Sending...') : copy.send}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onDone}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </div>
      </Form>
    </div>
  )
}

/**
 * One letter, read before the workflow sends it (e.g. a release Legal must
 * approve): filled in from the claim exactly as it will go out.
 */
export function ClaimLetterPreviewDialog({
  claimUuid,
  letter,
  onClose,
}: {
  claimUuid: string
  letter: { template_id: number; name: string } | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const preview = useComposerTemplatePreview(
    'claim',
    letter ? claimUuid : undefined,
    letter?.template_id ?? null,
  )
  return (
    <Dialog open={letter !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            {letter?.name}
          </DialogTitle>
          <p className="text-[12px] text-[var(--ink-3)]">
            {t(
              'claimLetter.previewHint',
              'Filled in from this claim, exactly as it will be sent.',
            )}
          </p>
        </DialogHeader>
        {preview.isPending ? (
          <div className="flex h-[200px] items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : preview.isError || !preview.data ? (
          <p className="py-8 text-center text-[13px] text-[var(--ink-3)]">
            {t('claimLetter.previewFailed', 'This letter could not be previewed.')}
          </p>
        ) : (
          <LetterPreview
            subject={preview.data.subject}
            bodyHtml={preview.data.body_html}
            pdfBase64={preview.data.pdf_base64 ?? ''}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * The letter page in the send form. Starts from the letter as the record fills
 * it in; a part nobody has typed into keeps following the record (a different
 * addressee picked, a fill-in answered). Preview PDF prints exactly what is on
 * the page.
 */
function LetterPageField({
  value,
  onChange,
  data,
  loading,
  recordType,
  recordUuid,
  templateId,
  error,
}: {
  value: LetterPageValue
  onChange: (next: LetterPageValue) => void
  data: ComposerTemplatePreview | undefined
  loading: boolean
  recordType: string
  recordUuid: string
  templateId: number
  error?: string
}) {
  const { t } = useTranslation()
  const [view, setView] = useState<'page' | 'pdf'>('page')
  const [printed, setPrinted] = useState<LetterEdit | undefined>(undefined)
  const pdf = useComposerTemplatePreview(
    recordType,
    recordUuid,
    view === 'pdf' ? templateId : null,
    undefined,
    printed,
  )

  const defaults = data?.header
  const rendered = data?.rendered_body_html ?? ''
  // Latest value and setter, for an effect that runs after later renders.
  const latest = useRef({ value, onChange })
  useEffect(() => {
    latest.current = { value, onChange }
  })
  useEffect(() => {
    if (!defaults) return
    const current = latest.current.value
    const next: LetterPageValue = {
      ...current,
      date: current.date || defaults.date,
      reference: current.reference || defaults.reference,
      name: current.touched.name ? current.name : defaults.name,
      address: current.touched.address ? current.address : defaults.address,
      body: current.touched.body ? current.body : rendered,
    }
    if (JSON.stringify(next) !== JSON.stringify(current)) latest.current.onChange(next)
  }, [defaults?.date, defaults?.reference, defaults?.name, defaults?.address, rendered, defaults])

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-[12px] text-[var(--ink-3)]">
        <Loader2 size={14} className="animate-spin" />
        {t('claimLetter.loadingPreview', 'Rendering the letter...')}
      </div>
    )
  }

  return (
    <Tabs
      value={view}
      onValueChange={(next) => {
        if (next === 'pdf') {
          setPrinted({
            ...(value.touched.body ? { body_html_override: value.body } : {}),
            letter_header: {
              date: value.date,
              name: value.name,
              address: value.address,
              reference: value.reference,
            },
          })
        }
        setView(next === 'pdf' ? 'pdf' : 'page')
      }}
    >
      <TabsList variant="pill" className="w-fit">
        <TabsTrigger value="page" data-testid="letter-tab-page">
          <Pencil className="mr-1 h-3 w-3" />
          {t('claimLetter.tabWrite', 'Write')}
        </TabsTrigger>
        <TabsTrigger value="pdf" data-testid="letter-tab-pdf">
          {t('claimLetter.tabPdf', 'Preview PDF')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="page">
        <LetterPage
          value={value}
          onChange={onChange}
          letterhead={data?.letterhead}
          referenceLabel={defaults?.reference_label ?? ''}
          error={error}
        />
      </TabsContent>
      <TabsContent value="pdf">
        {pdf.isPending ? (
          <div className="flex items-center gap-2 py-6 text-[12px] text-[var(--ink-3)]">
            <Loader2 size={14} className="animate-spin" />
            {t('claimLetter.loadingPreview', 'Rendering the letter...')}
          </div>
        ) : pdf.data?.pdf_base64 ? (
          <iframe
            src={`data:application/pdf;base64,${pdf.data.pdf_base64}`}
            title={t('claimCompose.letterPreview', 'Letter preview')}
            data-testid="compose-letter-pdf"
            className="h-[min(64vh,640px)] w-full rounded-md border-[0.5px] border-[var(--rule)] bg-white"
          />
        ) : (
          <p className="rounded-md border-[0.5px] border-dashed border-[var(--rule)] px-4 py-5 text-center text-[12px] text-[var(--ink-3)]">
            {t('claimLetter.previewFailed', 'This letter could not be previewed.')}
          </p>
        )}
      </TabsContent>
    </Tabs>
  )
}

/** The letter as it will be read: the PDF for a letter, the email otherwise. */
function LetterPreview({
  subject,
  bodyHtml,
  pdfBase64,
}: {
  subject: string
  bodyHtml: string
  pdfBase64: string
}) {
  const { t } = useTranslation()
  // Sandboxed: tenant-authored HTML must not reach the app's origin.
  const emailFrame = (
    <iframe
      sandbox=""
      srcDoc={bodyHtml}
      title={subject || t('claimCompose.emailPreview', 'Email preview')}
      className="h-[min(52vh,480px)] w-full rounded-md border-[0.5px] border-[var(--rule)] bg-white"
    />
  )
  if (!pdfBase64) return emailFrame
  return (
    <Tabs defaultValue="letter">
      <TabsList>
        <TabsTrigger value="letter">{t('claimCompose.tabLetter', 'Letter')}</TabsTrigger>
        <TabsTrigger value="email">{t('claimCompose.tabEmail', 'Email')}</TabsTrigger>
      </TabsList>
      <TabsContent value="letter">
        <iframe
          src={`data:application/pdf;base64,${pdfBase64}`}
          title={t('claimCompose.letterPreview', 'Letter preview')}
          data-testid="compose-letter-pdf"
          className="h-[min(52vh,480px)] w-full rounded-md border-[0.5px] border-[var(--rule)] bg-white"
        />
      </TabsContent>
      <TabsContent value="email">{emailFrame}</TabsContent>
    </Tabs>
  )
}

/** Edit the authored source for this one send; the template is untouched. */
function LetterEditForm({
  initialSubject,
  initialBody,
  onTyped,
  onApply,
  onCancel,
}: {
  initialSubject: string
  initialBody: string
  /** The sender has typed; the form stops following the record. */
  onTyped: () => void
  onApply: (edit: LetterEdit) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  // The latest callback, for field handlers made once per template.
  const typedRef = useRef(onTyped)
  useEffect(() => {
    typedRef.current = onTyped
  })
  const bodyRequired = t('claimCompose.bodyRequired', 'The letter needs some content.')
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'subject',
        label: t('claimLetter.subjectLabel', 'Subject'),
        value: initialSubject,
        required: t('claimCompose.subjectRequired', 'Add a subject.'),
        onChange: () => typedRef.current(),
      },
      {
        type: 'custom',
        name: 'body',
        value: initialBody,
        validationSchema: z.string().refine((html) => html.replace(/<[^>]*>/g, '').trim().length > 0, {
          message: bodyRequired,
        }),
        render: (ctx) => {
          const error = ctx.errors.body as string | undefined
          return (
            <div className="flex flex-col gap-1.5">
              <EmailEditor
                label={t('claimLetter.bodyLabel', 'Message')}
                value={String(ctx.values.body ?? '')}
                onChange={(html) => {
                  ctx.setFieldValue('body', html)
                  if (error) ctx.setFieldError('body', undefined)
                  typedRef.current()
                }}
                defaultMode="visual"
                // Whoever sends it types into it; never HTML. A table layout
                // reads as paragraphs for this one send.
                typingOnly
                minHeight={220}
              />
              {error && <p className="text-[11px] text-[var(--status-error)]">{error}</p>}
            </div>
          )
        },
      },
    ],
    [initialSubject, initialBody, bodyRequired, t],
  )
  const { Form } = useForm(
    { name: 'compose-letter-edit', submit: 'footer', fields },
    (values) =>
      onApply({
        subject_override: String(values.subject ?? ''),
        body_html_override: String(values.body ?? ''),
      }),
    [initialSubject, initialBody],
  )
  return (
    <Form className="flex flex-col gap-3">
      <p className="text-[11px] text-[var(--ink-3)]">
        {t('claimLetter.editHint', 'Changes apply to this letter only. The template is not modified.')}
      </p>
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm">
          {t('claimLetter.applyEdit', 'Save changes')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </Form>
  )
}

/** Who the letter goes to: the record's own parties, or anyone typed in. */
function RecipientPicker({
  value,
  error,
  loading,
  parties,
  mailAvailable,
  onChange,
}: {
  value: RecipientsValue
  error?: string
  loading: boolean
  parties: Party[]
  /** Letters only, and only once the tenant has a mailroom inbox. */
  mailAvailable: boolean
  onChange: (next: RecipientsValue) => void
}) {
  const { t } = useTranslation()
  const delivery: LetterDelivery = mailAvailable ? value.delivery : 'email'
  const byPost = delivery === 'mail'

  function toggleParty(key: string) {
    const picked = value.parties.includes(key)
    onChange({
      ...value,
      parties: picked ? value.parties.filter((k) => k !== key) : [...value.parties, key],
    })
  }

  function switchDelivery(next: LetterDelivery) {
    // Drop anyone the new way cannot reach, so nobody is silently skipped.
    const keep = new Set(parties.filter((p) => reachable(p, next)).map((p) => p.key))
    onChange({ ...value, delivery: next, parties: value.parties.filter((k) => keep.has(k)) })
  }

  return (
    <div
      className={`flex flex-col gap-2.5 rounded-md ${
        error ? 'p-2 ring-1 ring-[var(--status-error)]' : ''
      }`}
      data-testid="compose-recipients"
    >
      {mailAvailable && (
        <div className="flex flex-col gap-1.5">
          <p className={SECTION_LABEL}>{t('claimCompose.sendBy', 'Send by')}</p>
          <Tabs
            value={delivery}
            onValueChange={(next) => switchDelivery(next === 'mail' ? 'mail' : 'email')}
          >
            <TabsList variant="pill" className="w-fit" data-testid="compose-delivery">
              <TabsTrigger value="email" data-testid="compose-delivery-email">
                {t('claimCompose.byEmail', 'Email')}
              </TabsTrigger>
              <TabsTrigger value="mail" data-testid="compose-delivery-mail">
                {t('claimCompose.byMail', 'Mail')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {byPost && (
            <p className="text-[11px] text-[var(--ink-3)]">
              {t(
                'claimCompose.mailHint',
                'The letter goes in the next mail batch: the mailroom gets every letter to post once a day, ready to print and send to each address.',
              )}
            </p>
          )}
        </div>
      )}

      <p className={error ? `${SECTION_LABEL} text-[var(--status-error)]` : SECTION_LABEL}>
        {t('claimLetter.sendTo', 'Send to')}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
          <Loader2 size={14} className="animate-spin" />
          {t('claimLetter.loadingContacts', 'Loading contacts...')}
        </div>
      ) : (
        parties.map((party) => {
          const canReach = reachable(party, delivery)
          return (
            <Label
              key={party.key}
              data-testid="compose-party"
              className={`flex items-start gap-3 rounded-[8px] border-[0.5px] px-3 py-2.5 font-normal ${
                value.parties.includes(party.key)
                  ? 'border-[var(--green)] bg-[var(--green-bg-muted)]'
                  : 'border-[var(--rule)] bg-[var(--bg-light)]'
              } ${canReach ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
            >
              <Checkbox
                checked={value.parties.includes(party.key)}
                disabled={!canReach}
                onCheckedChange={() => toggleParty(party.key)}
                className="mt-0.5"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-[13px] text-[var(--ink)]">{party.name}</span>
                  {party.label && <Badge variant="secondary">{party.label}</Badge>}
                  {party.audience === 'internal' && (
                    <Badge variant="outline">{t('claimCompose.internal', 'Internal')}</Badge>
                  )}
                </span>
                {(party.email || !byPost) && (
                  <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
                    <Mail className="h-3 w-3 shrink-0 text-[var(--ink-3)]" />
                    <span className="truncate">
                      {party.email || t('claimCompose.noEmail', 'No email on file')}
                    </span>
                  </span>
                )}
                {(party.address || byPost) && (
                  <span className="flex min-w-0 items-start gap-1.5 text-[12px] text-[var(--ink-2)]">
                    <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-[var(--ink-3)]" />
                    <span className="min-w-0">
                      {party.address
                        ? party.address.replace(/\n/g, ', ')
                        : t('claimCompose.noAddress', 'No postal address')}
                    </span>
                  </span>
                )}
              </span>
            </Label>
          )
        })
      )}

      <Label className="flex cursor-pointer items-center gap-3 rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] px-3 py-2.5 text-[13px] font-normal text-[var(--ink)]">
        <Checkbox
          checked={value.someoneElse}
          data-testid="compose-someone-else"
          onCheckedChange={() => onChange({ ...value, someoneElse: !value.someoneElse })}
        />
        {t('claimCompose.someoneElse', 'Someone else')}
      </Label>

      {value.someoneElse && byPost && (
        <div className="flex flex-col gap-2 pl-7">
          <Input
            value={value.name}
            onChange={(event) => onChange({ ...value, name: event.target.value })}
            placeholder={t('claimLetter.namePlaceholder', 'Full name')}
            className="h-9 text-[13px]"
          />
          <Textarea
            value={value.address}
            onChange={(event) => onChange({ ...value, address: event.target.value })}
            placeholder={t('claimCompose.addressPlaceholder', 'Street, town and postcode')}
            rows={3}
            className="text-[13px]"
            data-testid="compose-other-address"
          />
        </div>
      )}

      {value.someoneElse && !byPost && (
        <div className="flex flex-col gap-2 pl-7 md:flex-row">
          <Input
            value={value.email}
            onChange={(event) => onChange({ ...value, email: event.target.value })}
            placeholder={t('claimLetter.emailPlaceholder', 'name@company.com')}
            type="email"
            className="h-9 text-[13px]"
          />
          <Input
            value={value.name}
            onChange={(event) => onChange({ ...value, name: event.target.value })}
            placeholder={t('claimLetter.namePlaceholder', 'Full name')}
            className="h-9 text-[13px]"
          />
        </div>
      )}

      {error && (
        <p className="text-[11px] text-[var(--status-error)]" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * Files to send with the letter: the record's own, or uploaded for this send.
 * Only claims take files today, which is why the empty and loading lines
 * still name a claim.
 */
function LetterAttachments({
  recordType,
  recordUuid,
  value,
  error,
  onChange,
}: {
  recordType: string
  recordUuid: string
  value: AttachmentPick[]
  error?: string
  onChange: (next: AttachmentPick[]) => void
}) {
  const { t } = useTranslation()
  const recordFiles = useSubjectAttachments(recordType, recordUuid)
  const upload = useUploadSendAttachment()
  const inputRef = useRef<HTMLInputElement>(null)
  // An upload finishes after later renders: read the latest value and setter.
  const latest = useRef({ value, onChange })
  useEffect(() => {
    latest.current = { value, onChange }
  })

  const files = (recordFiles.data ?? []).filter((f) => !f.is_deleted)
  const uploads = value.filter((f) => f.upload)

  function toggle(file: { uuid: string; file_name: string; file_size: number }) {
    const picked = value.some((f) => f.uuid === file.uuid)
    onChange(
      picked
        ? value.filter((f) => f.uuid !== file.uuid)
        : [...value, { uuid: file.uuid, name: file.file_name, size: file.file_size, upload: false }],
    )
  }

  async function uploadFiles(list: FileList | null) {
    const added: AttachmentPick[] = []
    for (const file of Array.from(list ?? [])) {
      try {
        const stored = await upload.mutateAsync(file)
        added.push({ uuid: stored.uuid, name: stored.file_name, size: stored.file_size, upload: true })
      } catch (err) {
        toastApiError(err, t('claimCompose.uploadFailed', 'The file could not be uploaded.'))
      }
    }
    if (inputRef.current) inputRef.current.value = ''
    if (added.length) latest.current.onChange([...latest.current.value, ...added])
  }

  return (
    <div
      className={`flex flex-col gap-2.5 rounded-md ${
        error ? 'p-2 ring-1 ring-[var(--status-error)]' : ''
      }`}
      data-testid="compose-attachments"
    >
      <p className={error ? `${SECTION_LABEL} text-[var(--status-error)]` : SECTION_LABEL}>
        {t('claimCompose.attachments', 'Attachments')}
      </p>
      <p className="text-[11px] text-[var(--ink-3)]">
        {t(
          'claimCompose.attachmentsHint',
          'Files you pick go with the letter and stay on its note. Up to {{count}} files, {{size}} MB in total.',
          { count: MAX_ATTACHMENTS, size: MAX_ATTACHMENT_MB },
        )}
      </p>

      {recordFiles.isPending ? (
        <div className="flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
          <Loader2 size={14} className="animate-spin" />
          {t('claimCompose.loadingFiles', 'Loading the claim\'s files...')}
        </div>
      ) : files.length === 0 ? (
        <p className="pl-1 text-[12px] text-[var(--ink-3)]">
          {t('claimCompose.noClaimFiles', 'This claim has no files yet.')}
        </p>
      ) : (
        <div className="flex max-h-[180px] flex-col gap-2 overflow-y-auto">
          {files.map((file) => (
            <Label
              key={file.uuid}
              data-testid="compose-claim-file"
              className="flex cursor-pointer items-center gap-2.5 pl-1 text-[13px] font-light text-[var(--ink)]"
            >
              <Checkbox
                checked={value.some((f) => f.uuid === file.uuid)}
                onCheckedChange={() => toggle(file)}
              />
              <Paperclip size={12} className="shrink-0 text-[var(--ink-3)]" />
              <span className="min-w-0 truncate">{file.file_name}</span>
              <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
                {formatBytes(file.file_size)}
              </span>
            </Label>
          ))}
        </div>
      )}

      {uploads.map((file) => (
        <div
          key={file.uuid}
          data-testid="compose-uploaded-file"
          className="flex items-center gap-2.5 pl-1 text-[13px] font-light text-[var(--ink)]"
        >
          <Upload size={12} className="shrink-0 text-[var(--ink-3)]" />
          <span className="min-w-0 truncate">{file.name}</span>
          <span className="shrink-0 text-[11px] text-[var(--ink-3)]">{formatBytes(file.size)}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={t('claimCompose.removeFile', 'Remove {{name}}', { name: file.name })}
            onClick={() => onChange(value.filter((f) => f.uuid !== file.uuid))}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}

      <div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACH_ACCEPT}
          className="hidden"
          data-testid="compose-attach-input"
          onChange={(event) => void uploadFiles(event.target.files)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="mr-1.5 h-3.5 w-3.5" />
          )}
          {upload.isPending
            ? t('claimCompose.uploading', 'Uploading...')
            : t('claimCompose.uploadFile', 'Upload a file')}
        </Button>
      </div>

      {error && (
        <p className="text-[11px] text-[var(--status-error)]" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
